import React, { useState, useMemo } from "react";
import { createColumnHelper, ExpandedState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { DataTable } from "@/components/ui/virtualizedTable";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Edit2, Trash2, AlertCircle, Lock, Unlock, Trash } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import {
  RiskCropProfileBreakout,
  useListBreakouts,
  useDeleteBreakout,
  getBreakoutLabel,
} from "@/lib/api/risk/profiles/breakouts";
import {
  RiskCropProfile, useDeleteProfile
} from "@/lib/api/risk/profiles";
import { BreakoutFormDialog } from "./BreakoutFormDialog";
import { EditParentProfileDialog } from "./EditParentProfileDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ProfileTableProps {
  profile: RiskCropProfile;
  customerId: string;
  onSuccess?: () => void;
}

interface TableRow {
  id: string;
  profile_id: string;
  type: "parent" | "breakout";
  label: string;
  acreage: string | number | null;
  projected_yield: string | number | null;
  actual_yield: string | number | null;
  projected_production: string | number | null;
  actual_production: string | number | null;
  cost_per_acre: string | number | null;
  is_enabled: boolean;
  is_editable: boolean;
  breakout?: RiskCropProfileBreakout;
}

export function ProfileTable({
  profile,
  customerId,
  onSuccess,
}: ProfileTableProps) {
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingBreakout, setEditingBreakout] = useState<RiskCropProfileBreakout | null>(null);
  const [deletingBreakout, setDeletingBreakout] = useState<RiskCropProfileBreakout | null>(null);
  const [showEditParentDialog, setShowEditParentDialog] = useState(false);
  const [showDeleteProfileDialog, setShowDeleteProfileDialog] = useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);

  const queryClient = useQueryClient();
  const deleteProfile = useDeleteProfile();

  // Fetch breakouts for this profile
  const breakoutsResult = useListBreakouts({ profileId: profile.id });
  const deleteBreakout = useDeleteBreakout();

  const breakouts = breakoutsResult?.data?.data || [];
  const hasBreakouts = breakouts.filter((b: RiskCropProfileBreakout) => b.is_enabled).length > 0;
  const isLoading = breakoutsResult.isLoading;

  const handleDeleteProfile = async () => {
    setIsDeletingProfile(true);
    try {
      const result = await deleteProfile(profile.id);
      if (result.success) {
        toast({ title: "Profile deleted successfully" });
        setShowDeleteProfileDialog(false);
      } else {
        toast({
          title: "An error occurred while deleting the profile",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting profile:", error);
      toast({
        title: "An error occurred while deleting the profile",
        variant: "destructive",
      });
    } finally {
      setIsDeletingProfile(false);
    }
  }


  const handleDeleteBreakout = async () => {
    if (!deletingBreakout) return;

    try {
      const result = await deleteBreakout(deletingBreakout.id);
      if (result.success) {
        toast({ title: "Breakout deleted successfully" });
        setDeletingBreakout(null);
      } else {
        toast({
          title: "Failed to delete breakout",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting breakout:", error);
      toast({
        title: "An error occurred while deleting the breakout",
        variant: "destructive"
      });
    }
  };


  // Prepare table data
  const tableData: TableRow[] = [];

  if (!hasBreakouts) {
    // Show parent as editable row when no breakouts
    tableData.push({
      id: profile.id,
      profile_id: profile.id,
      type: "parent",
      label: `${profile.commodity_name} ${profile.crop_year}`,
      acreage: profile.acreage ? safeParseDecimal({})(profile.acreage).toNumber() : null,
      projected_yield: profile.projected_yield ? safeParseDecimal({})(profile.projected_yield).toNumber() : null,
      actual_yield: profile.actual_yield ? safeParseDecimal({})(profile.actual_yield).toNumber() : null,
      projected_production: profile.projected_total_production ? safeParseDecimal({})(profile.projected_total_production).toNumber() : null,
      actual_production: profile.actual_total_production ? safeParseDecimal({})(profile.actual_total_production).toNumber() : null,
      cost_per_acre: profile.cost_per_acre ? safeParseDecimal({})(profile.cost_per_acre).toNumber() : null,
      is_enabled: profile.is_enabled,
      is_editable: true,
    });
  } else {
    // Show breakouts when they exist
    breakouts.forEach((breakout: RiskCropProfileBreakout) => {
      tableData.push({
        id: breakout.id,
        profile_id: profile.id,
        type: "breakout",
        label: getBreakoutLabel(breakout.breakout_type, breakout.breakout_value),
        acreage: breakout.is_enabled ? (breakout.acreage ? safeParseDecimal({})(breakout.acreage).toNumber() : null) : null,
        projected_yield: breakout.is_enabled ? (breakout.projected_yield ? safeParseDecimal({})(breakout.projected_yield).toNumber() : null) : null,
        actual_yield: breakout.is_enabled ? (breakout.actual_yield ? safeParseDecimal({})(breakout.actual_yield).toNumber() : null) : null,
        projected_production: breakout.is_enabled ? (breakout.projected_total_production ? safeParseDecimal({})(breakout.projected_total_production).toNumber() : null) : null,
        actual_production: breakout.is_enabled ? (breakout.actual_total_production ? safeParseDecimal({})(breakout.actual_total_production).toNumber() : null) : null,
        cost_per_acre: breakout.is_enabled ? (breakout.cost_per_acre ? safeParseDecimal({})(breakout.cost_per_acre).toNumber() : null) : null,
        is_enabled: breakout.is_enabled,
        is_editable: true,
        breakout,
      });
    });
  }

  const columnHelper = createColumnHelper<TableRow>();

  const columns: any[] = useMemo(() => [
    columnHelper.accessor("label", {
      id: "label",
      header: ({ table }: any) => (
        <>
          <button
            onClick={table.getToggleAllRowsExpandedHandler()}
            className="mr-2"
          >
            {table.getIsAllRowsExpanded() ? '▼' : '▶'}
          </button>
          Type
        </>
      ),
      aggregatedCell: () => `${profile.commodity_name} - ${profile.crop_year}`,
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            paddingLeft: `${row.depth * 2}rem`,
          }}
          className="flex items-center gap-2"
        >
          {row.getCanExpand() ? (
            <button
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: 'pointer' }}
            >
              {row.getIsExpanded() ? '▼' : '▶'}
            </button>
          ) : null}
          <span className="font-medium">{getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("profile_id", {
      id: "profile_id",
      header: "Profile Id",
    }),
    columnHelper.accessor("acreage", {
      id: "acreage",
      header: "Acreage",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.acreage)}
        </div>
      ),
    }),
    columnHelper.accessor("projected_yield", {
      id: "projected_yield",
      header: "Projected Yield",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.projected_yield)}
        </div>
      ),
    }),
    columnHelper.accessor("actual_yield", {
      id: "actual_yield",
      header: "Actual Yield",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.actual_yield)}
        </div>
      ),
    }),
    columnHelper.accessor("projected_production", {
      id: "projected_production",
      header: "Projected Production",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.projected_total_production)}
        </div>
      ),
    }),
    columnHelper.accessor("actual_production", {
      id: "actual_production",
      header: "Actual Production",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.actual_total_production)}
        </div>
      ),
    }),
    columnHelper.accessor("cost_per_acre", {
      id: "cost_per_acre",
      header: "Cost Per Acre",
      aggregationFn: "sum",
      cell: (info) => (
        <div className="text-right">
          {formatNumberToDisplay(info.getValue())}
        </div>
      ),
      aggregatedCell: (info) => (
        <div className="text-right font-bold">
          {formatNumberToDisplay(profile.cost_per_acre)}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const row = info.row.original;

        if (row.type === "parent" && !hasBreakouts) {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowEditParentDialog(true)}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          );
        }

        if (row.type === "breakout" && row.breakout) {
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingBreakout(row.breakout!)}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeletingBreakout(row.breakout!)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        }

        return null;
      },
      aggregatedCell: () => null,
    }),
  ], [hasBreakouts, setShowEditParentDialog, setEditingBreakout, setDeletingBreakout]);

  if (isLoading) {
    return <div className="p-4">Loading profile data...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header with info and add button */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            {profile.commodity_name} - {profile.crop_year}
          </h3>
          {hasBreakouts ? (
            <div className="flex items-center gap-2 text-sm text-amber-600 mt-1">
              <AlertCircle className="h-4 w-4" />
              Profile has breakouts. May only edit breakdowns and not profile directly.
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-green-600 mt-1">
              <AlertCircle className="h-4 w-4" />
              No breakouts. You can edit the parent profile directly or add breakouts.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mr-6">
          <Button
            onClick={() => setShowDeleteProfileDialog(true)}
            className="gap-2"
            size="sm"
          >
            <Trash className="h-4 w-4" />
            Delete Profile
          </Button>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="gap-2"
            size="sm"
          >
            <Plus className="h-4 w-4" />
            Add Breakout
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        defaultGroupingOpen
        columns={columns}
        data={tableData}
        height="400px"
        defaultSorting={[{ id: "label", desc: false }]}
        defaultGrouping={hasBreakouts ? ["profile_id"] : undefined}
        columnVisibility={{ profile_id: false }}
      />

      {/* Create/Edit Breakout Dialog */}
      {(showCreateDialog || editingBreakout) && (
        <BreakoutFormDialog
          isOpen={showCreateDialog || !!editingBreakout}
          onClose={() => {
            setShowCreateDialog(false);
            setEditingBreakout(null);
          }}
          profileId={profile.id}
          customerId={customerId}
          commodityName={profile.commodity_name}
          cropYear={profile.crop_year}
          isEdit={!!editingBreakout}
          breakout={editingBreakout || undefined}
          onSuccess={() => {
            setShowCreateDialog(false);
            setEditingBreakout(null);
            if (onSuccess) onSuccess();
          }}
        />
      )}

      {/* Edit Parent Profile Dialog */}
      {showEditParentDialog && (
        <EditParentProfileDialog
          isOpen={showEditParentDialog}
          onClose={() => setShowEditParentDialog(false)}
          profile={profile}
          onSuccess={() => {
            setShowEditParentDialog(false);
            if (onSuccess) onSuccess();
          }}
        />
      )}

      {/* Delete Profile Confirmation Dialog */}
      <Dialog
        open={showDeleteProfileDialog}
        onOpenChange={(open: boolean) => !open && !isDeletingProfile && setShowDeleteProfileDialog(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the {profile.commodity_name} {profile.crop_year} profile?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteProfileDialog(false)} disabled={isDeletingProfile}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProfile} disabled={isDeletingProfile}>
              {isDeletingProfile ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Breakout Confirmation Dialog */}
      <Dialog
        open={!!deletingBreakout}
        onOpenChange={(open: boolean) => !open && setDeletingBreakout(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Breakout</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this breakout? This action cannot
              be undone, but the history will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingBreakout(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBreakout}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
